
import { useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';
import Contact from '../../home/components/Contact';
import { testimonials } from '../../../mocks/testimonials';

export default function PhiladelphiaPage() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Smooth scroll to the contact section
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Services and industries data
  const allServices = [
    'Web Design & Development',
    'AI Integration & Automation',
    'Search Engine Optimization (SEO)',
    'Social Media Marketing',
    'Google Local Services Ads (LSA)',
    'Pay-Per-Click Advertising (PPC)',
    'Reputation Management',
    'Content Marketing',
    'Email Marketing',
    'Lead Generation',
  ];

  const industries = [
    { name: 'Electrical Contractors', icon: 'ri-flashlight-line' },
    { name: 'HVAC Services', icon: 'ri-temp-cold-line' },
    { name: 'Landscaping Companies', icon: 'ri-plant-line' },
    { name: 'Pest Control Services', icon: 'ri-bug-line' },
    { name: 'Plumbing Contractors', icon: 'ri-drop-line' },
    { name: 'Roofing Companies', icon: 'ri-home-4-line' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-600/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
              Philadelphia Contractor Digital Marketing Agency
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Dominate the Philadelphia market with specialized digital marketing strategies designed
              exclusively for contractors. Generate high‑quality leads and grow your contracting business
              in the City of Brotherly Love.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={scrollToContact}
                className="inline-flex items-center px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer"
              >
                Get Your Free Marketing Audit <i className="ri-arrow-right-line ml-2" />
              </button>
              <a
                href="tel:2153963311"
                className="inline-flex items-center px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2" /> (215) 396-3311
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-6">
                Digital Marketing for Philadelphia Contractors
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Philadelphia and the surrounding counties represent a thriving market for contractors.
                With over 1.6 million residents in the city and 6 million in the metro area, the demand
                for quality contracting services spans from historic home renovations to modern
                commercial projects.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Our local expertise helps Philadelphia contractors stand out in neighborhoods from
                Center City to the Main Line, from South Philly to the Northeast, ensuring you capture
                customers exactly when they need your services.
              </p>
              <div className="space-y-4">
                {[
                  'Rank #1 on Google for high‑value Philadelphia contractor searches',
                  'Generate qualified leads from all Philadelphia neighborhoods and suburbs',
                  'Build a dominant online presence in the competitive Philly market',
                  'Attract both residential and commercial clients with proven strategies',
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <p className="text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/707c2aa149d8553833ea7972917f2d4b.jpg"
                alt="Philadelphia City Skyline"
                className="w-full h-auto rounded-2xl shadow-2xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-6">
              Why Philadelphia Contractors Choose Million Dollar Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're based in the Philadelphia area and specialize exclusively in contractor marketing,
              understanding the unique challenges of growing a contracting business in our local market.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-map-pin-2-line',
                title: 'Local Philadelphia Expertise',
                description:
                  'We know the Philadelphia market inside and out—from Center City to the suburbs. We understand local search patterns and how to capture customers in your specific service area.',
              },
              {
                icon: 'ri-team-line',
                title: 'Contractor‑Only Focus',
                description:
                  'Unlike general marketing agencies, we only work with contractors. We understand your business model, seasonal patterns, and what it takes to generate high‑quality leads.',
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Proven Results',
                description:
                  'Our strategies are built on data and results. We focus on metrics that matter: qualified leads, booked appointments, and revenue growth for your contracting business.',
              },
              {
                icon: 'ri-trophy-line',
                title: 'Track Record of Success',
                description:
                  "We've helped dozens of Philadelphia contractors dominate their local markets and achieve consistent 6‑figure and 7‑figure revenues through strategic digital marketing.",
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Dedicated Support',
                description:
                  "You'll work directly with experienced marketing strategists who understand your business and are committed to your success in the Philadelphia market.",
              },
              {
                icon: 'ri-rocket-line',
                title: 'Cutting‑Edge Technology',
                description:
                  'We leverage the latest AI tools, automation, and marketing technology to give you a competitive advantage and maximize your marketing ROI.',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-3xl text-white`} />
                </div>
                <h3 className="font-heading text-xl font-bold text-black mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              Philadelphia Contractor Industries We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized digital marketing strategies for every type of contractor in the Philadelphia
              metro area
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all border-l-4 border-red-600 group hover:bg-red-600 cursor-pointer"
              >
                <div className="w-14 h-14 bg-red-600 group-hover:bg-white rounded-xl flex items-center justify-center mb-4 transition-colors">
                  <i
                    className={`${industry.icon} text-2xl text-white group-hover:text-red-600 transition-colors`}
                  />
                </div>
                <h3 className="font-heading text-xl font-bold text-black group-hover:text-white mb-2 transition-colors">
                  {industry.name}
                </h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors">
                  Dominate Philadelphia searches for {industry.name.toLowerCase()}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              Complete Digital Marketing Services for Philadelphia Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to dominate the Philadelphia contractor market and generate consistent,
              high‑quality leads
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allServices.map((service, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl hover:shadow-lg transition-all border-l-4 border-red-600"
              >
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-sm" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-black">{service}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-black mb-4">
              Success Stories from Philadelphia Contractors
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how we've helped contractors across Philadelphia grow their businesses with proven
              digital marketing strategies
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl shadow-lg">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-black">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call‑to‑Action */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Dominate the Philadelphia Contractor Market?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get your free marketing audit and discover exactly how to generate more leads, book more
            jobs, and grow your contracting business in Philadelphia.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={scrollToContact}
              className="inline-flex items-center px-8 py-4 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
            >
              Claim Your Free Audit Now <i className="ri-arrow-right-line ml-2" />
            </button>
            <a
              href="tel:2153963311"
              className="inline-flex items-center px-8 py-4 bg-black text-white font-semibold rounded-lg hover:bg-gray-900 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2" /> Call (215) 396-3311
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <div id="contact">
        <Contact />
      </div>

      <Footer />
    </div>
  );
}
